import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, ShieldCheck, Calendar } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine } from "recharts";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { Card, CardContent } from "./ui/card";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const shortDate = (iso, lang) => {
    try {
        const d = new Date(iso + "T12:00:00");
        return d.toLocaleDateString(lang === "es" ? "es-US" : "en-US", { month: "short", day: "numeric" });
    } catch (e) {
        return iso;
    }
};

const PredictionsCard = ({ connected }) => {
    const { language } = useApp();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        if (!connected) { setLoading(false); return; }
        api.get("/predictions")
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch(() => { if (!cancelled) setData(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [connected]);

    if (!connected) {
        return (
            <Card className="rounded-3xl border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm" data-testid="predictions-card">
                <CardContent className="p-6 md:p-8">
                    <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase">
                        {t(language, "pred_eyebrow")}
                    </div>
                    <div className="mt-2 font-display font-bold text-2xl md:text-3xl tracking-tight text-[hsl(var(--foreground))]">
                        {t(language, "pred_title")}
                    </div>
                    <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">{t(language, "pred_needs_bank")}</p>
                </CardContent>
            </Card>
        );
    }

    if (loading || !data) {
        return (
            <Card className="rounded-3xl border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm" data-testid="predictions-card">
                <CardContent className="p-6 md:p-8">
                    <div className="h-4 w-40 bg-[hsl(var(--muted))]/40 rounded animate-pulse" />
                    <div className="mt-3 h-8 w-64 bg-[hsl(var(--muted))]/40 rounded animate-pulse" />
                </CardContent>
            </Card>
        );
    }

    const upcoming = data.upcoming_bills || [];
    const forecast = (data.balance_forecast || []).map((p) => ({
        ...p,
        label: shortDate(p.date, language),
    }));
    const risk = !!data.overdraft_risk;

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
        >
            <Card className="rounded-3xl border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm" data-testid="predictions-card">
                <CardContent className="p-6 md:p-8">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase">
                                {t(language, "pred_eyebrow")}
                            </div>
                            <h3 className="mt-2 font-display font-bold text-2xl md:text-3xl tracking-tight text-[hsl(var(--foreground))]">
                                {t(language, "pred_title")}
                            </h3>
                        </div>
                    </div>

                    {/* Alert banner */}
                    <div
                        className={`mt-5 rounded-2xl border p-4 flex items-start gap-3 ${
                            risk
                                ? "border-red-300/50 bg-red-50 dark:bg-red-500/10 text-red-800 dark:text-red-200"
                                : "border-emerald-300/50 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-200"
                        }`}
                        data-testid={risk ? "predictions-overdraft-alert" : "predictions-safe-alert"}
                    >
                        {risk ? <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" /> : <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" />}
                        <div className="flex-1">
                            <div className="text-sm font-bold">
                                {t(language, risk ? "pred_overdraft_title" : "pred_safe_title")}
                            </div>
                            <div className="mt-1 text-xs md:text-sm opacity-90">
                                {t(language, risk ? "pred_overdraft_desc" : "pred_safe_desc")}
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Upcoming bills list */}
                        <div>
                            <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase mb-3">
                                {t(language, "pred_upcoming")}
                            </div>
                            {upcoming.length === 0 ? (
                                <p className="text-sm text-[hsl(var(--muted-foreground))]">{t(language, "pred_no_bills")}</p>
                            ) : (
                                <ul className="space-y-2" data-testid="predictions-upcoming-list">
                                    {upcoming.slice(0, 6).map((b, i) => (
                                        <li
                                            key={`${b.merchant}-${b.expected_date}-${i}`}
                                            className="flex items-center justify-between gap-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2.5"
                                            data-testid="predictions-bill-row"
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-8 h-8 rounded-lg bg-[hsl(var(--secondary))] grid place-items-center shrink-0">
                                                    <Calendar className="w-4 h-4 text-[hsl(var(--primary))]" />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-semibold text-[hsl(var(--foreground))] truncate">{b.merchant}</div>
                                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{shortDate(b.expected_date, language)}</div>
                                                </div>
                                            </div>
                                            <div className="text-sm font-bold tabular-nums text-[hsl(var(--foreground))]">
                                                {fmt(b.amount)}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Balance forecast area chart */}
                        <div>
                            <div className="text-[11px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase mb-3">
                                {t(language, "pred_forecast")}
                            </div>
                            <div className="h-[180px]" data-testid="predictions-forecast-chart">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={forecast} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="predGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={risk ? "#EF4444" : "#7DD3B0"} stopOpacity={0.35} />
                                                <stop offset="100%" stopColor={risk ? "#EF4444" : "#7DD3B0"} stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} interval="preserveStartEnd" axisLine={false} tickLine={false} minTickGap={24} />
                                        <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${Math.round(v)}`)} width={44} />
                                        <Tooltip
                                            formatter={(v) => fmt(v)}
                                            labelFormatter={(l) => l}
                                            contentStyle={{ borderRadius: 10, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }}
                                        />
                                        <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.4} strokeDasharray="3 3" />
                                        <Area type="monotone" dataKey="balance" stroke={risk ? "#EF4444" : "#22C55E"} strokeWidth={2.5} fill="url(#predGrad)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
};

export default PredictionsCard;
